import React, { useState, useEffect, useRef } from "react";
import { Header, Container, Tab } from "semantic-ui-react";
import _ from "lodash";
import AWS from "aws-sdk";
import { fetchRegisteredAccounts } from "services/accounts";
import { updateSubscriptions } from "services/api-catalog";
import { AppDetail } from "components/Admin/DeveloperInfo/AppDetail";
import { Target } from "components/Admin/DeveloperInfo/Target";
import { Profile } from "components/Admin/DeveloperInfo/Profile";
import { PendingRowWithoutTable } from "components/Admin/PendingRow";
import { HostDetail } from "components/Admin/DeveloperInfo/HostDetail";
import { PortalMessage } from "components/Admin/PortalMessage";
import { handleMessage } from "utils/function-utils";
import {
  getProfileList,
  getAppDetail,
  updateApp,
  addApp,
  deleteApp,
  getEnvide,
} from "services/developer";
import { NotFound } from "components/NotFound";

const userPoolId = _.get(window, "config.userPoolId");

const DeveloperDetail = ({ userDetail, loaded }) => {
  const PendingDetail = () => {
    const DEFAULT_LENGTH = 2;
    return (
      <div style={{ marginBottom: "20px" }}>
        {Array.from({ length: DEFAULT_LENGTH }).map((_, index) => {
          return <PendingRowWithoutTable key={index} />;
        })}
      </div>
    );
  };

  return (
    <Container className="user-detail-container">
      {!loaded ? (
        <PendingDetail />
      ) : (
        <div className="user-detail-content">
          <img alt="user" src="/custom-content/user.png" />
          <div className="info-container">
            <div>{userDetail && userDetail.email}</div>
            <div className="user-status">
              {userDetail && userDetail.UserStatus}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

const DeveloperInfo = () => {
  const [userDetail, setUserDetail] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [appDetail, setAppDetail] = useState(null);
  const [appLoaded, setAppLoaded] = useState(false);
  const [showSuccessMes, setShowSuccessMes] = useState(false);
  const [showFailMes, setShowFailMes] = useState(false);
  const [profileList, setProfileList] = useState([]);
  const editTagRef = useRef(null);
  const emailRef = useRef(null);
  const apiKeyRef = useRef(null);
  const identityIdRef = useRef(null);
  const [ENVIDE, setENVIDE] = useState({});

  useEffect(() => {
    (async () => {
      const userDetail = await getUserDetail();
      const envide = await getEnvide();
      setENVIDE(envide);
      setUserDetail(userDetail);
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    emailRef.current = new URLSearchParams(window.location.search).get("email");
    (async () => {
      const [appDetail, accountList] = await Promise.all([
        getAppDetail({ UserName: emailRef.current }),
        fetchRegisteredAccounts(),
      ]);
      setAppDetail(appDetail);
      getApiKey(accountList);
      setAppLoaded(true);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (appDetail && appDetail.AppId) {
        if (editTagRef.current) return;
        const profileList = await getProfileList({
          host: "",
          filter: "",
          Scope: "",
          Stage: "",
          MES: "",
        });
        setProfileList(profileList);
      }
    })();
  }, [appDetail, userDetail]);

  const getUserDetail = async () => {
    const cognitoidentityserviceprovider =
      new AWS.CognitoIdentityServiceProvider();
    const email = new URLSearchParams(window.location.search).get("email");
    return cognitoidentityserviceprovider
      .listUsers({
        UserPoolId: userPoolId,
        Filter: `email='${email}'`,
      })
      .promise()
      .then((res) => {
        const detail = res.Users[0];
        if (detail && detail.Username) {
          detail.email = email;
          return detail;
        }
        return {};
      })
      .catch(() => Promise.resolve(null));
  };

  const getApiKey = (accountList) => {
    const filteredAccount = accountList.filter(
      (account) => account.EmailAddress === emailRef.current
    );
    const apiKey = filteredAccount[0]?.ApiKeyId;
    const identityId = filteredAccount[0]?.IdentityId;
    apiKeyRef.current = apiKey;
    identityIdRef.current = identityId;
    return apiKey;
  };

  const onSaveUpdateApp = async (params) => {
    setAppLoaded(false);
    let res;
    Object.assign(params, { apiKey: apiKeyRef.current });
    if (params.operation === "addApp") {
      res = await addApp(params);
    }
    if (params.operation === "updateApp") {
      editTagRef.current = true;
      res = await updateApp(params);
    }
    if (params.operation === "deleteApp") {
      res = await deleteApp(params);
    }
    if (res.success) {
      // update status after operation
      const appDetail = await getAppDetail({ UserName: emailRef.current });
      await updateSubscriptions(true).catch((err) => Promise.resolve(err));
      setAppDetail(appDetail);
    }
    setAppLoaded(true);
    handleMessage({
      success: res.success,
      operation: params.operation,
      setShowSuccessMes,
      setShowFailMes,
      content: res.message,
    });
  };

  const handleMessageInfo = ({ success, operation, content }) => {
    handleMessage({
      success,
      operation,
      setShowSuccessMes,
      setShowFailMes,
      content,
    });
  };

  if (userDetail && !userDetail.Username) {
    return <NotFound />;
  }

  return (
    <Container id="developer-info" fluid style={{ padding: "2em" }}>
      <Header as="h1">Developer Information</Header>
      <PortalMessage
        showSuccessMes={showSuccessMes}
        showFailMes={showFailMes}
        setShowSuccessMes={setShowSuccessMes}
        setShowFailMes={setShowFailMes}
      />
      <DeveloperDetail userDetail={userDetail} loaded={loaded} />
      <AppDetail
        email={emailRef}
        identityId={identityIdRef.current}
        appLoaded={appLoaded}
        appDetail={appDetail}
        onSaveUpdateApp={onSaveUpdateApp}
        ENVIDE={ENVIDE}
      />
      <Tab
        panes={[
          {
            menuItem: "MES15",
            pane: (
              <Tab.Pane className="tab-container" key="MES15">
                <Target
                  editTagRef={editTagRef}
                  appDetail={appDetail}
                  handleMessage={handleMessageInfo}
                />
                <Profile
                  profileList={profileList}
                  appDetail={appDetail}
                  handleMessage={handleMessageInfo}
                />
              </Tab.Pane>
            ),
          },
          {
            menuItem: "MES16",
            pane: (
              <Tab.Pane className="tab-container" key="MES16">
                <HostDetail
                  apiKey={apiKeyRef.current}
                  editTagRef={editTagRef}
                  profileList={profileList}
                  appDetail={appDetail}
                  handleMessage={handleMessageInfo}
                  ENVIDE={ENVIDE}
                />
              </Tab.Pane>
            ),
          },
        ]}
        renderActiveOnly={false}
      />
    </Container>
  );
};

export default DeveloperInfo;
